import cn from "clsx";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Input } from "@/common/components/form/input";
import { debounce } from "@/common/utils/debounce";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { formatPrice } from "@/features/product/hooks/use-price";
import { SelectionBadge, TableHeader } from "./components";

const DEFAULT_PRICE_VALUE = "--";

const PriceValue: React.FC<{
  value?: string | JSX.Element | null;
  defaultValue?: string;
  className?: string;
}> = ({ value, defaultValue, className = "" }) => {
  return (
    <div
      className={cn(
        "text-grey-90 flex h-12 items-center pr-2 text-sm leading-none md:pr-4 md:text-base",
        className,
      )}
    >
      {value || defaultValue}
    </div>
  );
};

export const CustomQuantityForm: React.FC<{
  setQuantity: (q: number) => void;
  max: number;
  min: number;
  className?: string;
}> = ({ setQuantity, max, min, className }) => {
  const t = useTranslations("custom-attributes-forms");
  const { pricingResult } = useStoreProduct();
  const {
    register,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<{ quantity: number }>();
  const quantity = watch("quantity");

  const priceValue = useMemo(() => {
    if (isNaN(quantity) || !pricingResult?.salePrice) {
      return null;
    }

    const { salePrice } = pricingResult;

    return {
      totalPrice: formatPrice(salePrice.total),
      unitPrice: formatPrice(salePrice.unit, "en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
      }),
    };
  }, [quantity, pricingResult]);

  const debouncedOnChange = useCallback(debounce(setQuantity, 1500), []);

  const handleChange = () => {
    const quantity = getValues("quantity");
    debouncedOnChange(quantity);
  };

  useEffect(() => {
    // if custom quantity in the list but the value is hidden in scrollable box
    // scroll to row
    const container = document.querySelector(".quantity-table-rows");
    const selectedRow = document.querySelector(".selected-quantity");
    if (!container || !selectedRow) return;
    const containerRect = container.getBoundingClientRect();
    const selectedRect = selectedRow.getBoundingClientRect();
    const isValueVisible =
      selectedRect.top <= containerRect.top
        ? containerRect.top - selectedRect.top <= selectedRect.height
        : selectedRect.bottom - containerRect.bottom <= selectedRect.height;
    !isValueVisible &&
      selectedRow.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [quantity]);

  return (
    <div className={cn("flex flex-col", className)}>
      <TableHeader quantityText={t("label-quantity")} />

      <div className="relative flex justify-between">
        <Input
          variant="solid"
          type="number"
          {...register("quantity", {
            valueAsNumber: true,
            onChange: handleChange,
            validate: {
              isPositive: (v) => (v && Number(v) >= 0) || t("error-positive"),
              max: (v) => Number(v) <= max || t("error-max"),
              min: (v) => Number(v) >= min || t("error-min"),
            },
          })}
          error={errors.quantity && errors.quantity.message!}
          className="w-[80px] md:w-[120px]"
          inputClassName={cn("!md:text-base h-12 !text-sm", {
            "!bg-berry-10 !border-[2px] !border-berry-90": priceValue,
          })}
          labelClassName="!text-xs !leading-none !font-normal"
        />

        <PriceValue
          value={priceValue?.unitPrice}
          defaultValue={DEFAULT_PRICE_VALUE}
          className="z-10 w-[52px] md:w-2/12"
        />
        <PriceValue
          value={priceValue?.totalPrice}
          defaultValue={DEFAULT_PRICE_VALUE}
          className="z-10 w-[92px] md:w-2/12"
        />
        <PriceValue
          value={(priceValue && <SelectionBadge className="mx-auto flex-none" />) || ""}
          className="z-10 w-[82px] md:w-4/12"
        />

        {priceValue && (
          <span className="bg-berry-10 absolute right-0 top-0 z-0 h-12 w-[calc(100%_-_80px_-_5px)] md:w-[calc(100%_-_120px_-_10px)]" />
        )}
      </div>
    </div>
  );
};
